<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">My Notifications</h5>
        <p class="page-sub">Your alerts and activity updates</p>
      </div>
      <button v-if="unreadCount > 0" class="btn-mark-all" @click="markAllRead" :disabled="markingAll">
        <font-awesome-icon icon="check-double" />
        Mark all as read
      </button>
    </div>

    <!-- Summary chips -->
    <div v-if="!loading && notifications.length" class="summary-bar">
      <span class="chip chip--total">{{ meta.total || notifications.length }} total</span>
      <span class="chip chip--unread" v-if="unreadCount">{{ unreadCount }} unread</span>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="notif-list">
      <div v-for="n in 6" :key="n" class="sk-item">
        <div class="sk-icon"></div>
        <div class="sk-body">
          <div class="sk-line sk-line--lg"></div>
          <div class="sk-line sk-line--sm"></div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="!notifications.length" class="empty-box">
      <div class="empty-illustration">
        <font-awesome-icon icon="bell-slash" class="empty-icon" />
      </div>
      <p class="empty-title">No notifications yet</p>
      <p class="empty-sub">You'll see alerts here when there's activity related to your attendance, leave, payroll, and approvals.</p>
    </div>

    <!-- List -->
    <div v-else class="notif-list">
      <div
        v-for="n in notifications"
        :key="n.id"
        class="notif-item"
        :class="{ 'notif-item--unread': !n.read_at }"
        @click="markRead(n)"
      >
        <div class="notif-icon-wrap" :class="iconClass(n.data?.type)">
          <font-awesome-icon :icon="iconName(n.data?.type)" />
        </div>
        <div class="notif-body">
          <p class="notif-msg">{{ n.data?.message || 'Notification' }}</p>
          <p class="notif-meta">
            <span class="notif-time">{{ timeAgo(n.created_at) }}</span>
            <span v-if="n.data?.type" class="notif-type">{{ formatType(n.data.type) }}</span>
          </p>
        </div>
        <div v-if="!n.read_at" class="unread-dot" title="Unread"></div>
      </div>
    </div>

    <!-- Load more -->
    <div v-if="hasMore && !loading" class="load-more-row">
      <button class="btn-load-more" @click="loadMore" :disabled="loadingMore">
        <font-awesome-icon v-if="loadingMore" icon="spinner" spin />
        {{ loadingMore ? 'Loading…' : 'Load more' }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const TYPE_META = {
  leave_request:       { icon: 'umbrella-beach', cls: 'icon--blue' },
  leave_approved:      { icon: 'check-circle',   cls: 'icon--green' },
  leave_rejected:      { icon: 'times-circle',   cls: 'icon--red' },
  missing_clock_out:   { icon: 'clock',           cls: 'icon--orange' },
  payroll_lock:        { icon: 'lock',            cls: 'icon--red' },
  payroll_processed:   { icon: 'money-bill-wave', cls: 'icon--green' },
  shift_assigned:      { icon: 'calendar-check',  cls: 'icon--blue' },
  announcement:        { icon: 'bullhorn',         cls: 'icon--indigo' },
  default:             { icon: 'bell',            cls: 'icon--gray' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)    return 'Just now';
  if (diff < 3600)  return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default {
  name: 'MyNotificationsPage',
  data() {
    return {
      loading: false,
      loadingMore: false,
      markingAll: false,
      notifications: [],
      unreadCount: 0,
      currentPage: 1,
      lastPage: 1,
      meta: {},
    };
  },
  computed: {
    hasMore() { return this.currentPage < this.lastPage; },
  },
  mounted() {
    this.fetchNotifications(1);
  },
  methods: {
    async fetchNotifications(page = 1) {
      if (page === 1) { this.loading = true; this.notifications = []; }
      else { this.loadingMore = true; }
      try {
        const { data } = await axios.get('/hr/notifications', { params: { length: 20, page } });
        const resp = data.data?.notifications;
        const items = resp?.data || [];
        this.notifications = page === 1 ? items : [...this.notifications, ...items];
        this.currentPage = resp?.current_page || 1;
        this.lastPage    = resp?.last_page    || 1;
        this.meta        = { total: resp?.total };
        this.unreadCount = data.data?.unread_count ?? this.unreadCount;
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load notifications');
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    loadMore() { this.fetchNotifications(this.currentPage + 1); },
    async markRead(n) {
      if (n.read_at) return;
      try {
        await axios.put(`/hr/notifications/${n.id}/read`);
        n.read_at = new Date().toISOString();
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      } catch (e) { /* silent */ }
    },
    async markAllRead() {
      this.markingAll = true;
      try {
        await axios.put('/hr/notifications/read-all');
        this.notifications.forEach(n => { n.read_at = n.read_at || new Date().toISOString(); });
        this.unreadCount = 0;
      } catch (e) {
        this.$toast?.error('Failed to mark all as read');
      } finally {
        this.markingAll = false;
      }
    },
    iconName(type) { return (TYPE_META[type] || TYPE_META.default).icon; },
    iconClass(type) { return (TYPE_META[type] || TYPE_META.default).cls; },
    formatType(type) {
      if (!type) return '';
      return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    timeAgo,
  },
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:16px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-mark-all { display:inline-flex; align-items:center; gap:7px; height:36px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; color:#004AAD; font-size:13px; font-weight:600; cursor:pointer; transition:all .15s; }
.btn-mark-all:hover:not(:disabled) { background:#004AAD; color:#fff; border-color:#004AAD; }
.btn-mark-all:disabled { opacity:.5; cursor:not-allowed; }

.summary-bar { display:flex; gap:8px; margin-bottom:16px; }
.chip        { display:inline-block; font-size:11px; font-weight:700; padding:3px 10px; border-radius:20px; }
.chip--total  { background:#f1f5f9; color:#64748b; }
.chip--unread { background:#dbeafe; color:#1d4ed8; }

/* Skeleton */
.sk-item  { display:flex; gap:12px; padding:16px; background:#fff; border:1px solid #e4eaf2; border-radius:12px; margin-bottom:8px; }
.sk-icon  { width:40px; height:40px; border-radius:10px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; flex-shrink:0; }
.sk-body  { flex:1; display:flex; flex-direction:column; gap:8px; justify-content:center; }
.sk-line  { border-radius:4px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
.sk-line--lg { height:14px; width:70%; }
.sk-line--sm { height:11px; width:35%; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Empty */
.empty-box         { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:14px; }
.empty-illustration{ width:88px; height:88px; border-radius:50%; background:#f0f7ff; display:flex; align-items:center; justify-content:center; }
.empty-icon        { font-size:36px; color:#004AAD; opacity:.45; }
.empty-title       { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.empty-sub         { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:380px; line-height:1.6; }

/* List */
.notif-list { display:flex; flex-direction:column; gap:6px; }

.notif-item         { display:flex; align-items:flex-start; gap:14px; padding:16px; background:#fff; border:1px solid #e4eaf2; border-radius:12px; cursor:pointer; transition:all .15s; }
.notif-item:hover   { box-shadow:0 2px 12px rgba(0,0,0,.07); border-color:#c8d8f0; }
.notif-item--unread { background:#f0f7ff; border-color:#c8dbf7; }
.notif-item--unread:hover { background:#e0efff; }

.notif-icon-wrap { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.icon--blue   { background:#dbeafe; color:#1d4ed8; }
.icon--green  { background:#dcfce7; color:#16a34a; }
.icon--orange { background:#ffedd5; color:#ea580c; }
.icon--red    { background:#fee2e2; color:#dc2626; }
.icon--gray   { background:#f1f5f9; color:#64748b; }

.notif-body { flex:1; min-width:0; }
.notif-msg  { font-size:13px; color:#1a2b45; margin:0 0 5px; line-height:1.45; }
.notif-meta { display:flex; align-items:center; gap:10px; margin:0; }
.notif-time { font-size:11px; color:#7a90b0; }
.notif-type { font-size:10px; font-weight:700; color:#94a3b8; background:#f1f5f9; padding:2px 7px; border-radius:20px; }

.unread-dot { width:9px; height:9px; border-radius:50%; background:#004AAD; flex-shrink:0; margin-top:6px; }

.load-more-row  { display:flex; justify-content:center; padding:16px 0; }
.btn-load-more  { display:inline-flex; align-items:center; gap:7px; height:36px; padding:0 24px; border:1px solid #dde5f0; border-radius:8px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; cursor:pointer; transition:all .15s; }
.btn-load-more:hover:not(:disabled) { background:#004AAD; color:#fff; border-color:#004AAD; }
.btn-load-more:disabled { opacity:.5; cursor:not-allowed; }
</style>
